/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, ReactNode } from "react";
// eslint-disable-next-line import/no-unresolved
import { MenuItem } from "primereact/menuitem";

interface NavContextType {
  breadcrumbs: MenuItem[] | undefined;
  setBreadcrumbs: (breadcrumbs: MenuItem[] | undefined) => void;
}

const NavContext = createContext<NavContextType | undefined>(undefined);

interface NavContextProviderProps {
  children: ReactNode;
}

/**
 * NavContextProvider
 *
 * Provides a context for managing navigation breadcrumbs throughout the application.
 * Components can use this to override or supply breadcrumbs that will be displayed
 * in the UserNavBar.
 *
 * @param {NavContextProviderProps} props - The provider props
 * @returns {JSX.Element} The provider component
 */
export const NavContextProvider: React.FC<NavContextProviderProps> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<MenuItem[] | undefined>(undefined);

  const value: NavContextType = {
    breadcrumbs,
    setBreadcrumbs,
  };

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
};

/**
 * useNavContext Hook
 *
 * Custom hook to access the navigation context. Provides methods to get and set
 * breadcrumbs for the navigation bar.
 *
 * @throws {Error} If used outside of NavContextProvider
 * @returns {NavContextType} The navigation context value
 *
 * @example
 * ```tsx
 * const { breadcrumbs, setBreadcrumbs } = useNavContext();
 *
 * // Set breadcrumbs in a component
 * useEffect(() => {
 *   setBreadcrumbs([
 *     { label: 'Home', command: () => navigate('/') },
 *     { label: 'Current Page' }
 *   ]);
 *
 *   return () => setBreadcrumbs(undefined); // Clear on unmount
 * }, []);
 * ```
 */
export const useNavContext = (): NavContextType => {
  const context = useContext(NavContext);

  if (!context) {
    throw new Error("useNavContext must be used within a NavContextProvider");
  }

  return context;
};
